import React from 'react';
import { MadadCategory, MarketplaceType } from 'utils/entities';
import { Link } from 'components/link';
import { useLocale } from 'locale';
import * as Styled from './styled';
import LeadAgentIcon from 'assets/svg/circle-wavy-check.svg';
import { Route } from 'config/routes';
import ChevronIcon from 'assets/svg/arrow-chevron-left.svg';
import MadadInfoCarousel from './carousel';
import { SpacingScale } from 'ds/theme/spacing';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import theme from 'ds/theme/default';
import { useScreenBreakpoint } from 'consts/breakpoints';


export enum MadadInfoHeaderType {
  OfficeName = 'officeName',
  AgentInfo = 'agentInfo',
  AgentLogo = 'agentLogo',
  OfficeLogo = 'officeLogo',
  MadadBadge = 'madadBadge',
}

interface CommonMadadHeaderItem {
  type: MadadInfoHeaderType;
  ml?: SpacingScale;
}

interface MadadOfficeLogoItem extends CommonMadadHeaderItem {
  type: MadadInfoHeaderType.OfficeLogo;
  officeLogo: string;
  officeId: string;
}

interface MadadAgentLogoItem extends CommonMadadHeaderItem {
  type: MadadInfoHeaderType.AgentLogo;
  agentLogo: string;
  agentId: string;
}

interface MadadAgentInfoItem extends CommonMadadHeaderItem {
  type: MadadInfoHeaderType.AgentInfo;
  agentName: string;
  agentHint: string;
}

interface MadadBadgeItem extends CommonMadadHeaderItem {
  type: MadadInfoHeaderType.MadadBadge;
  madadCategory: MadadCategory;
}

interface OfficeNameItem extends CommonMadadHeaderItem {
  type: MadadInfoHeaderType.OfficeName;
  officeName: string;
}

export type MadadHeaderItem = MadadOfficeLogoItem
 | MadadBadgeItem
 | MadadAgentInfoItem
 | OfficeNameItem
 | MadadAgentLogoItem;


interface DeveloperSectionProps {
  title: string;
  description: string;
  listingsIds: string[];
  madadHeaderItems: MadadHeaderItem[];
  moreLinkRoute: Route;
  moreLinkParamsId: string;
  moreLinkCopy: string;
  carouselKey: symbol;
  from: string;
  marketplace: MarketplaceType;
  type: 'agent' | 'office';
  trackingEventSource?: string;
}

const MadadHeaderItem: React.FC<MadadHeaderItem & { trackingEventSource?: string; }> = (props) => {
  const constructImageURI = useImageUrlBuilder();
  const isMobile = useScreenBreakpoint() < 3;
  const { t } = useLocale();

  switch (props.type) {
    case MadadInfoHeaderType.AgentLogo:
      const agentLogo = constructImageURI(props.agentLogo, {
        width: 105,
        height: 105,
      });
      return (
        <div style={{ position: 'relative', marginLeft: theme.spacing(props.ml) || 0 }}>
          <Link
            data-auto="agent-link"
            routeName={Route.AgentPage}
            routeParams={{ id: props.agentId, ...(props.trackingEventSource ? { source: props.trackingEventSource } : null) }}
          >
            <Styled.AgentLogoWrapper>
              <img src={agentLogo} />
            </Styled.AgentLogoWrapper>
            <Styled.AgentLeadingLabel small>
              {t('developerPage.members.promoted.label')}
            </Styled.AgentLeadingLabel>
          </Link>
        </div>
      );
    case MadadInfoHeaderType.OfficeLogo:
      const officeLogo = constructImageURI(props.officeLogo);
      return (
        <div style={{ marginLeft: theme.spacing(props.ml) || 0 }}>
          <Link
            data-auto="office-link"
            routeName={Route.OfficePage}
            routeParams={{ id: props.officeId, ...(props.trackingEventSource ? { source: props.trackingEventSource } : null) }}
          >
            <Styled.OfficeLogoWrapper image={officeLogo} />
          </Link>
        </div>
      );
    case MadadInfoHeaderType.AgentInfo:
      return (
        <div style={{ marginLeft: theme.spacing(props.ml) || 0 }}>
          <Styled.AgentName weight="bold">
            {props.agentName}
            <LeadAgentIcon width={20} height={20} />
          </Styled.AgentName>
          <Styled.AgentHint isMobile={isMobile}>
            {props.agentHint}
          </Styled.AgentHint>
        </div>
      );
    case MadadInfoHeaderType.MadadBadge:
      const categoryToImagePath: Record<MadadCategory, string> = {
        'top_10': '/agents/top-green-mode-b-x3.png',
        'top_10_gold': '/agents/top-gold-mode-b-x3.png',
        'winner': null,
      };
      const path = categoryToImagePath[props.madadCategory];
      return path ? (
        <div style={{ marginLeft: theme.spacing(props.ml) || 0 }}>
          <img src={constructImageURI(path)} style={{ display: 'block', height: '80px' }} />
        </div>
      ) : null;
    case MadadInfoHeaderType.OfficeName:
      return (
        <div style={{ marginLeft: theme.spacing(props.ml) || 0 }}>
          <Styled.OfficeName weight="bold">
            {props.officeName}
          </Styled.OfficeName>
        </div>
      );
    default:
      return null;
  }
};

export const MadadInfoSection: React.FC<DeveloperSectionProps> = (props) => {
  const {
    title,
    madadHeaderItems,
    description,
    moreLinkRoute,
    moreLinkParamsId,
    moreLinkCopy,
    carouselKey,
    marketplace,
    listingsIds,
    from,
    type,
    trackingEventSource,
  } = props;
  const isMobile = useScreenBreakpoint() < 3;
  const showCarousel = Boolean(listingsIds.length);

  return (
    <Styled.Root>
      <Styled.Header weight="bold">{title}</Styled.Header>
      <Styled.Body isMobile={isMobile}>
        <Styled.DeveloperInfo>
          {madadHeaderItems.map(i => <MadadHeaderItem key={i.type} {...i} trackingEventSource={trackingEventSource} />)}
        </Styled.DeveloperInfo>
        <div>
          {description ? (
            <Styled.DescriptionWrapper isMobile={isMobile}>
              <span dangerouslySetInnerHTML={{ __html: description }} />
            </Styled.DescriptionWrapper>
          ) : null}
          <Styled.StyledLink
            data-auto="agent-office-link"
            target="_blank"
            routeName={moreLinkRoute}
            routeParams={{ id: moreLinkParamsId, ...(trackingEventSource ? { source: trackingEventSource } : null) }}
          >
            <Styled.DeveloperLinkText weight="medium">
              {moreLinkCopy}
              <ChevronIcon />
            </Styled.DeveloperLinkText>
          </Styled.StyledLink>
        </div>
        {showCarousel ? (
          <MadadInfoCarousel
            listingsIds={listingsIds}
            marketplace={marketplace}
            carouselKey={carouselKey}
            moreLinkRoute={moreLinkRoute}
            type={type}
            from={from}
            moreLinkParamsId={moreLinkParamsId}
            trackingEventSource={trackingEventSource}
          />
        ) : null}
      </Styled.Body>
    </Styled.Root>
  );
};
